import { yupResolver } from '@hookform/resolvers/yup';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
// mui
import { Dialog, Grid, Step, StepLabel, Stepper, Typography } from '@mui/material';
// components
import ActionButtons from '../../components/action-buttons';
import { GeneralInfo } from '../../forms/steps/general-info';
import { Reminder } from '../../forms/steps/reminder';
// custom hooks
import { useCatchServerError } from 'hooks/useCatchServerError';
// api
import {
  ICostCode,
  ICostCodeWithSelections,
  ISelectionItem,
  SelectionStatusEnum,
  useCreateSelectionItemMutation,
  useUpdateSelectionItemMutation,
} from 'redux-store/api/selections';
// validation
import { selectionSchemas } from '../../forms/validation';
// helpers
import { ISelectionFormValues, ModalProps } from '../../utils/types';
import {
  getCreateSelectionRequestBody,
  getSelectionFormDefaultValues,
  getUpdateSelectionRequestBody,
} from './helpers';
// types
import { TIcon } from '../../../../utils/types';
// icons
import CompletedIcon from 'assets/icons/new/selection/completed.svg';
import GeneralInfoIcon from 'assets/icons/new/selection/info-active.svg';
import ReminderIcon from 'assets/icons/new/selection/reminder.svg';
// styles
import { useSyncSelectedProject } from 'hooks/useSyncSelectedProject';
import { useUserInfo } from 'hooks/useUserInfo';
import { useGetCompanyRolePermissionsMeQuery } from 'redux-store/api/company-roles';
import { ADMIN_ROLE_ID, OWNER_ROLE_ID } from 'utils/constants';
import { ProjectCostCodeSelect } from 'widgets/selections/components/cost-code-select';
import { getCostCodeOptionLabel } from 'widgets/selections/components/cost-code-select/cost-code-select/utils';
import { ProjectLocationSelect } from 'widgets/selections/components/location-select';
import { SelectionsActivityFeed } from 'widgets/selections/modals/components/selections-activity-feed/selections-activity-feed';
import { LockedItem } from '../../../../components/locked-item';
import { getStyles } from '../styles';
import { styles as stepperStyles } from './styles';

interface IProps extends ModalProps {
  selection?: ISelectionItem;
  isEdit?: boolean;
  costCode?: ICostCodeWithSelections;
  costCategory?: ICostCode['category'];
  projectId?: number;
}

const getCostCodeLabelDefaultValue = (
  selection?: ISelectionItem,
  costCode?: ICostCodeWithSelections,
  costCategory?: ICostCode['category']
): string | null =>
  costCode
    ? getCostCodeOptionLabel({
        categoryNumber: costCode.category.number,
        costCodeNumber: costCode.number,
        costCodeName: costCode.name,
      })
    : selection && costCategory
      ? getCostCodeOptionLabel({
          categoryNumber: costCategory.number,
          costCodeNumber: selection.costCode.number,
          costCodeName: selection.costCode.name,
        })
      : null;

enum DefaultStepsEnum {
  'GENERAL_INFO',
  'REMINDER',
}

type TDefaultStep = {
  id: DefaultStepsEnum;
  label: string;
  icon: TIcon;
};

interface IStepIconCustomProps {
  StepIcon: TIcon;
  active: boolean;
  completed: boolean;
  isEditing: boolean;
}

const DEFAULT_STEPS: TDefaultStep[] = [
  { id: DefaultStepsEnum.GENERAL_INFO, label: 'General Info', icon: GeneralInfoIcon },
  { id: DefaultStepsEnum.REMINDER, label: 'Reminder', icon: ReminderIcon },
];

const StepIconComponent: React.FC<IStepIconCustomProps> = ({
  active,
  completed,
  StepIcon,
  isEditing,
}) => {
  if (isEditing) {
    return <CompletedIcon />;
  }
  if (active) {
    return <StepIcon className="active" />;
  }
  if (completed) {
    return <CompletedIcon className="completed" />;
  }
  return <StepIcon className="not-completed" />;
};

const SelectionModal: FC<IProps> = ({
  open,
  handleClose,
  costCode,
  costCategory,
  selection,
  isEdit,
  projectId: projectIdProp,
}) => {
  const { projectId: projectIdFromParams } = useParams();
  const resolvedProjectId = projectIdProp || projectIdFromParams;
  const projectId = Number(resolvedProjectId);
  const { project } = useSyncSelectedProject(projectId);

  const { catchError } = useCatchServerError();

  const [activeStep, setActiveStep] = useState<DefaultStepsEnum>(DefaultStepsEnum.GENERAL_INFO);

  const { data: userPermissions } = useGetCompanyRolePermissionsMeQuery();

  const isFirstStep: boolean = useMemo(
    () => activeStep === DefaultStepsEnum.GENERAL_INFO,
    [activeStep]
  );

  const isActivityFeedVisible =
    userPermissions?.ENABLE_ACTIVITY_FEED?.canCreate &&
    userPermissions?.ENABLE_SELECTION_ACTIVITY_FEED?.canCreate;

  const styles = getStyles({ modalWidth: isEdit && isActivityFeedVisible ? '1250px' : '750px' });

  const isLastStep: boolean = useMemo(() => activeStep === DefaultStepsEnum.REMINDER, [activeStep]);

  const validationSchema = selectionSchemas[activeStep];

  const { currentRole } = useUserInfo();

  const canCreateLibrarySelection = userPermissions?.LIBRARY_SELECTIONS?.canCreate;
  const projectPhaseItemId = selection ? selection?.projectPhaseItemId : undefined;
  const projectPhaseId = selection ? selection?.projectPhaseId : undefined;

  const form = useForm<ISelectionFormValues>({
    defaultValues: getSelectionFormDefaultValues(
      projectId,
      isEdit,
      selection,
      costCode,
      canCreateLibrarySelection
    ),
    resolver: yupResolver(validationSchema),
    mode: isEdit ? 'all' : 'onChange',
  });

  const [createSelection, { isLoading: isCreateLoading }] = useCreateSelectionItemMutation();
  const [updateSelection, { isLoading: isUpdateLoading }] = useUpdateSelectionItemMutation();

  const onCloseModal = useCallback((): void => {
    setActiveStep(DefaultStepsEnum.GENERAL_INFO);
    form.reset();
    handleClose();
  }, [form.reset, handleClose]);

  const handleBack = useCallback(async (): Promise<void> => {
    if (isFirstStep) {
      onCloseModal();
    } else {
      if (isEdit) {
        const isStepValid = await form.trigger();
        if (isStepValid) {
          setActiveStep((prev) => prev - 1);
        }
      } else {
        setActiveStep((prev) => prev - 1);
      }
    }
  }, [isEdit, isFirstStep, onCloseModal, form.trigger]);

  const handleStepClick = (id: DefaultStepsEnum) => async (): Promise<void> => {
    if (isEdit) {
      const isStepValid = await form.trigger();
      if (isStepValid) {
        setActiveStep(id);
      }
    }
  };

  const handleSubmit = useCallback(
    async (formValues: ISelectionFormValues): Promise<void> => {
      if (isLastStep) {
        if (isEdit && selection) {
          updateSelection(getUpdateSelectionRequestBody(formValues, selection))
            .unwrap()
            .then(onCloseModal)
            .catch(catchError);
        } else {
          createSelection(getCreateSelectionRequestBody(formValues))
            .unwrap()
            .then(onCloseModal)
            .catch(catchError);
        }
      } else {
        const isStepValid = await form.trigger();
        if (isStepValid) {
          setActiveStep((prev) => prev + 1);
        }
      }
    },
    [isLastStep, form.trigger, isEdit, selection, onCloseModal]
  );

  const isSelectionApproved: boolean =
    !!selection && selection.status === SelectionStatusEnum.APPROVED;

  const isDescriptionEditDisable =
    isSelectionApproved && ![OWNER_ROLE_ID, ADMIN_ROLE_ID].includes(currentRole?.roleId);

  const [costCodeLabel, setCostCodeLabel] = useState<string | null>(
    getCostCodeLabelDefaultValue(selection, costCode, costCategory)
  );

  const [locationLabel, setLocationLabel] = useState<string | null>(
    selection ? selection.room.title : null
  );

  const [options, status] = form.watch(['options', 'status']);

  const moreThanOneOption = options?.length > 1;

  const selectedOption = options.find(({ isSelected }) => isSelected);

  const isSubmittingDisabled =
    (activeStep === DefaultStepsEnum.REMINDER &&
      !selectedOption &&
      moreThanOneOption &&
      status === SelectionStatusEnum.APPROVED) ||
    (activeStep === DefaultStepsEnum.REMINDER && isDescriptionEditDisable);

  const renderActionButtons = useCallback(
    () => (
      <ActionButtons
        backLabel={isFirstStep ? 'Cancel' : 'Back'}
        nextLabel={isLastStep ? 'Save' : 'Next'}
        handleBack={handleBack}
        loading={isCreateLoading || isUpdateLoading}
        disableSubmit={isSubmittingDisabled}
      />
    ),
    [isFirstStep, isLastStep, handleBack, isCreateLoading, isUpdateLoading, isSubmittingDisabled]
  );

  const renderCostCodeAndLocation = useCallback(
    () => (
      <>
        <ProjectCostCodeSelect
          name="costCodeId"
          label="Cost Code"
          projectId={projectId}
          selectedOptionName={costCodeLabel}
          setSelectedOptionName={setCostCodeLabel}
          disabled={isSelectionApproved}
        />

        <ProjectLocationSelect
          withAddingLocation
          withImportingLocation
          name="roomId"
          label="Item Location"
          projectId={projectId}
          selectedOptionName={locationLabel}
          setSelectedOptionName={setLocationLabel}
          disabled={isSelectionApproved}
        />
      </>
    ),
    [projectId, costCodeLabel, locationLabel, isSelectionApproved]
  );

  const renderMainContent = () => (
    <>
      <Grid sx={styles.selectionDialogHeaderContainer}>
        <Grid sx={styles.selectionDialogTitleWrapper}>
          <Typography sx={styles.dialogTitle}>
            {isEdit ? 'edit selection' : 'add selection'}
          </Typography>
          <Typography sx={styles.dialogSubtitle}>
            {isEdit ? 'Change selection description.' : 'Create selection description.'}
          </Typography>

          <Stepper activeStep={activeStep} sx={stepperStyles.stepper}>
            {DEFAULT_STEPS.map((step) => (
              <Step
                key={step.id}
                sx={isEdit ? stepperStyles.stepStyles : {}}
                onClick={handleStepClick(step.id)}
              >
                <StepLabel
                  StepIconComponent={({ active, completed }) => (
                    <StepIconComponent
                      active={active}
                      completed={completed}
                      StepIcon={step.icon}
                      isEditing={isEdit}
                    />
                  )}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Grid>

        {isSelectionApproved && <LockedItem />}
      </Grid>

      <Grid sx={[styles.dialogContent, stepperStyles.dialogContent]}>
        <FormProvider {...form}>
          {activeStep === DefaultStepsEnum.GENERAL_INFO && (
            <GeneralInfo
              projectId={projectId}
              isDescriptionEditDisable={isDescriptionEditDisable}
              renderActionButtons={renderActionButtons}
              renderCostCodeAndLocation={renderCostCodeAndLocation}
              handleSubmit={handleSubmit}
              isProjectSelection={true}
              isEdit={isEdit}
              isSelectionApproved={isSelectionApproved}
            />
          )}
          {activeStep === DefaultStepsEnum.REMINDER && (
            <Reminder
              projectId={projectId}
              renderActionButtons={renderActionButtons}
              handleSubmit={handleSubmit}
              isSelectionApproved={isSelectionApproved}
              projectPhaseItemId={projectPhaseItemId}
              projectPhaseId={projectPhaseId}
            />
          )}
        </FormProvider>
      </Grid>
    </>
  );

  const renderDialogContent = () => {
    if (!isActivityFeedVisible || !isEdit) return renderMainContent();

    return (
      <Grid id="selections-container" sx={stepperStyles.selectionsContainer}>
        <Grid sx={stepperStyles.mainContent}>{renderMainContent()}</Grid>
        <SelectionsActivityFeed selectionsItem={selection} profiles={project.profiles} projectId={projectId} />
      </Grid>
    );
  };

  return (
    <Dialog open={open} onClose={onCloseModal} sx={styles.dialog} className="size-m">
      {renderDialogContent()}
    </Dialog>
  );
};

export default SelectionModal;
